import React from "react";
import moment from "moment";
import { useSelector } from "react-redux";
import { useIntl, FormattedMessage } from "react-intl";
import { FormProvider, useForm } from "react-hook-form";

import ModalExtended from "components/ModalExtended";
import ErrorList from "components/ErrorList";
import {
  getCurrentSchoolYear,
  getPreviousSchoolYears,
  PurchaseEvent,
} from "data-handler/ducks/stores";
import { Blockquote } from "@wfp/ui";
import useStore from "helpers/useStore";
import VendorEdit from "components/CashToSchool/vendorEdit";
import "./_cash-to-school.scss";
import PurchaseInformation from "components/CashToSchool/purchaseInfo";
import CommoditiesEdit from "components/CashToSchool/commoditiesEdit";
import { isEmpty } from "lodash";
import {
  getVendorCategories,
  getAllApprovedVendors,
} from "data-handler/ducks/vendors";
import { getPurchaseOtherCostsList } from "data-handler/ducks/purchaseOtherCosts";
import { purchaseDetailCategory } from "SCConstants";

import {
  ERROR_MESSAGES_INCIDENTS,
  isReportApprovedOrSubmitted,
  isReportClosedOrRejected,
  schoolYearDateValidation,
} from "components/IncidentEdit";
import { RootState } from "data-handler/rootReducer";
import {
  getDetailedStockStatus,
  getEarliestUsageDateForPurchaseCommodity,
} from "helpers/stock";
import { getAllStores } from "data-handler/ducks/stores";

type Vendor = {
  id: number;
  name: string;
};
const storeToFormValues = (
  store: any,
  vendorCategories: any,
  vendorList: Vendor[],
  otherCost: any,
  purchased_at: any
) => {
  const values = JSON.parse(JSON.stringify(store)); // deep copy
  // Repeat purchases set purchased_at to true
  // If a repeat purchase use current date and time, if not, use the inputted data
  const date = purchased_at
    ? moment().format("YYYY-MM-DD")
    : moment(values.occurred_at).format("YYYY-MM-DD");

  values.date = date;

  // Vendor id gets sent to the be - we need the fe to store the label to display it when editing
  const vendorCategoryId = values.meta?.vendor_category_id;
  const vendorCategory = vendorCategories[vendorCategoryId];
  values.vendor_category = {
    label: vendorCategory?.name,
    value: vendorCategoryId,
  };

  values.total_paid = values.meta?.total_paid;
  values.receipt_no = values.meta?.receipt_no || "";

  const vendorId = values?.vendor;
  const vendor = vendorList
    .filter((vendor) => vendorId === vendor.id)
    .find((vendor) => vendor.name);

  if (vendorId) {
    values.vendor = {
      label: vendor?.name,
      value: vendorId,
    };
  }

  if (values.stock_events) {
    values.stock_events.forEach((commodity: any) => {
      commodity.comments = commodity.meta?.comments || "";
    });
  }

  if (!values.other_costs || values.other_costs?.length === 0) {
    values.other_costs = [{}];
  } else {
    // Other costs stores the id we need the label for the be
    values.other_costs
      .filter((other: any) => other.other_cost && other.total_paid)
      .forEach((other: any) => {
        const otherCostId = other.other_cost;
        const otherCostValue = otherCost[otherCostId];
        other.other_cost = {
          label: otherCostValue?.name,
          value: otherCostId,
        };
      });
  }
  return values;
};

const formValuesToStore = (
  values: any,
  purchaseCurrentStoreData?: PurchaseEvent
) => {
  const isInitialStock =
    purchaseCurrentStoreData?.meta?.is_initial_stock === true;

  // Merge `date` and `time` fields into `purchased_at` datetime
  values.occurred_at = moment(
    values.date + " " + moment().format("HH:mm:ss"),
    "YYYY-MM-DD HH:mm:ss"
  ).toISOString();

  const vendorCategoryId = values.vendor_category;
  values.vendor_category = vendorCategoryId.hasOwnProperty("value")
    ? vendorCategoryId.value
    : vendorCategoryId;
  if (values.vendor) {
    const vendorId = values.vendor;
    values.vendor = vendorId?.hasOwnProperty("value")
      ? vendorId.value
      : vendorId;
  }
  if (values.stock_events) {
    values.stock_events.forEach((commodity: any) => {
      commodity.category = purchaseDetailCategory;
      commodity.sign = 1;
      commodity.type = "PUR";
      if (typeof commodity.measure_unit === "object") {
        commodity.measure_unit = commodity.measure_unit.id;
      }
      if (!commodity.meta) commodity.meta = {};
      commodity.meta.comments = commodity.comments || "";
      delete commodity.comments;

      // Events of an initial purchase stock are flagged as carry-over, which
      // is how they are recognised as opening stock; the form does not carry
      // the event meta through, so the flag must be restamped on every save
      if (isInitialStock) {
        commodity.meta.is_carry_over = true;
      }
    });
  }

  if (!values.meta) values.meta = {};
  values.meta.receipt_no = values.receipt_no || "";
  values.meta.vendor_category_id = values.vendor_category || null;
  values.meta.total_paid = parseFloat(values.total_paid?.toString() || "0");
  values.meta.is_initial_stock = isInitialStock;
  delete values.receipt_no;
  delete values.vendor_category;
  delete values.total_paid;

  values.other_costs = (values.other_costs || [])
    .filter((other: any) => other.other_cost && other.total_paid)
    .map((other: any) => ({
      ...other,
      other_cost: other.other_cost?.value ?? other.other_cost,
    }));
  return values;
};

const ERROR_MESSAGES = {
  missingCommodity: (
    <FormattedMessage
      id="PurchaseEdit.missingCommodity"
      defaultMessage="Select a commodity"
    />
  ),
  missingQuantity: (
    <FormattedMessage
      id="PurchaseEdit.missingQuantity"
      defaultMessage="Enter a quantity"
    />
  ),
  negativeQuantity: (
    <FormattedMessage
      id="PurchaseEdit.negativeQuantity"
      defaultMessage="Modified quantity is invalid with current meal data"
    />
  ),
  missingPrice: (
    <FormattedMessage
      id="PurchaseEdit.missingPrice"
      defaultMessage="Enter the amount paid"
    />
  ),
  missingDate: (
    <FormattedMessage
      id="PurchaseEdit.missingDate"
      defaultMessage="Enter date of purchase"
    />
  ),
  missingTime: (
    <FormattedMessage
      id="PurchaseEdit.missingTime"
      defaultMessage="Enter time of purchase"
    />
  ),
  missingVendorType: (
    <FormattedMessage
      id="PurchaseEdit.missingType"
      defaultMessage="Select vendor type"
    />
  ),
  missingVendorName: (
    <FormattedMessage
      id="PurchaseEdit.missingName"
      defaultMessage="Vendor name is missing"
    />
  ),
  selectCreationOrExisting: (
    <FormattedMessage
      id="PurchaseEdit.vendorCreation"
      defaultMessage="Select if you want to create or use existing vendor"
    />
  ),
  missingGrandTotal: (
    <FormattedMessage
      id="PurchaseEdit.missingGrandTotal"
      defaultMessage="Enter grand total"
    />
  ),
  missingOtherCost: (
    <FormattedMessage
      id="PurchaseEdit.missingOtherCost"
      defaultMessage="Select an other cost"
    />
  ),
  missingOtherCostPrice: (
    <FormattedMessage
      id="PurchaseEdit.missingOtherCostPrice"
      defaultMessage="Enter the amount paid for other cost"
    />
  ),
  invalidOtherCostPrice: (
    <FormattedMessage
      id="PurchaseEdit.invalidOtherCostPrice"
      defaultMessage="Other cost price must be greater than 0"
    />
  ),
  purchaseDateAfterUsage: (
    <FormattedMessage
      id="PurchaseEdit.purchaseDateAfterUsage"
      defaultMessage="Cannot change purchase date to after a consumption or incident that uses this purchase"
    />
  ),
};

const validationResolver = (
  values: any,
  {
    state,
    currentYearStore,
    previousSchoolYears,
    purchaseCurrentStoreData,
    stockStatus,
  }: any
) => {
  const originalCommodities =
    purchaseCurrentStoreData?.stock_events ||
    purchaseCurrentStoreData?.commodities ||
    [];
  const modifiedCommodities = values.stock_events;

  /**
   * Returns the quantity change caused by the current edit
   * for given commodity id.
   */
  const getOriginalCommodityChange = (
    commodityId: number,
    category: string
  ) => {
    const original = originalCommodities.find(
      (c: { id: number; category: string }) =>
        c.id === commodityId && c.category === category
    );
    const modified = modifiedCommodities.find(
      (c: { id: number; category: string }) => c.id === commodityId
    );
    const originalQuantity = original ? Number(original.quantity) : 0;
    const modifiedQuantity = modified ? Number(modified.quantity) : 0;

    const change = originalQuantity - modifiedQuantity;

    return change;
  };

  // List of commodity ids that has negative stock after their
  // quantity are updated or removed.
  const negativeStockPurchaseDetailCommodities: Set<number> = new Set();
  for (const [commodityIdStr, latestQuantityStr] of Object.entries(
    stockStatus.purchasedetail
  )) {
    const commodityId = Number(commodityIdStr);
    const latestQuantity = Number(latestQuantityStr);
    const quantityChange = getOriginalCommodityChange(
      commodityId,
      purchaseDetailCategory
    );
    const resultQuantity = latestQuantity - quantityChange;

    if (resultQuantity < 0) {
      negativeStockPurchaseDetailCommodities.add(commodityId);
    }
  }

  const errors: Record<string, { message: JSX.Element }> = {};
  const isSelectedDateInSchoolYearValid = schoolYearDateValidation(
    values.date,
    currentYearStore,
    previousSchoolYears
  );
  if (!isSelectedDateInSchoolYearValid && values.date) {
    errors["date"] = {
      message: ERROR_MESSAGES_INCIDENTS["invalidDate"],
    };
  }
  if (isReportClosedOrRejected(state, values.date) && values.date) {
    errors["date"] = {
      message: ERROR_MESSAGES_INCIDENTS["reportClosedOrRejected"],
    };
  }
  if (isReportApprovedOrSubmitted(state, values.date) && values.date) {
    errors["date"] = {
      message: ERROR_MESSAGES_INCIDENTS["reportApprovedOrSubmitted"],
    };
  }

  // Check if purchase date is being changed to after a consumption/incident that uses it
  if (purchaseCurrentStoreData && values.date) {
    const allStores = getAllStores(state);
    const originalDate = purchaseCurrentStoreData.occurred_at;
    const newDate = values.date;

    if (originalDate && !moment(originalDate).isSame(newDate, "day")) {
      originalCommodities.forEach((commodity: any) => {
        if (commodity.id) {
          const earliestUsageDate = getEarliestUsageDateForPurchaseCommodity(
            allStores,
            commodity.id
          );

          if (
            earliestUsageDate &&
            moment(newDate).isAfter(earliestUsageDate, "day")
          ) {
            errors["date"] = {
              message: ERROR_MESSAGES["purchaseDateAfterUsage"],
            };
          }
        }
      });
    }
  }

  if (!values.vendor_category) {
    errors[`vendor_category`] = {
      message: ERROR_MESSAGES["missingVendorType"],
    };
  }

  if (!values.date) {
    errors[`date`] = {
      message: ERROR_MESSAGES["missingDate"],
    };
  }
  if (values.stock_events) {
    values.stock_events.forEach((commodity: any, i: number) => {
      if (!commodity.commodity && commodity.commodity === undefined) {
        errors[`stock_events[${i}].commodity`] = {
          message: ERROR_MESSAGES["missingCommodity"],
        };
      }
      // `quantity` is required on each commodity row
      if (!commodity.quantity) {
        errors[`stock_events[${i}].quantity`] = {
          message: ERROR_MESSAGES["missingQuantity"],
        };
      } // `price` is required on each commodity row

      if (negativeStockPurchaseDetailCommodities.has(commodity.id)) {
        errors[`stock_events[${i}].quantity`] = {
          message: ERROR_MESSAGES["negativeQuantity"],
        };
      }

      if (!commodity.total_paid) {
        errors[`stock_events[${i}].total_paid`] = {
          message: ERROR_MESSAGES["missingPrice"],
        };
      }
    });
  }

  if (values.other_costs) {
    values.other_costs.forEach((other: any, i: number) => {
      if (!other.other_cost && other.total_paid) {
        errors[`other_costs[${i}].other_cost`] = {
          message: ERROR_MESSAGES["missingOtherCost"],
        };
      }
      if (other.other_cost && !other.total_paid) {
        errors[`other_costs[${i}].total_paid`] = {
          message: ERROR_MESSAGES["missingOtherCostPrice"],
        };
      }
      if (other.other_cost && !(other.total_paid > 0)) {
        errors[`other_costs[${i}].total_paid`] = {
          message: ERROR_MESSAGES["invalidOtherCostPrice"],
        };
      }
    });
  }

  if (!values.total_paid) {
    errors[`total_paid`] = {
      message: ERROR_MESSAGES["missingGrandTotal"],
    };
  }
  if (!values.no_vendor_info && (values.vendor === "" || !values.vendor)) {
    errors[`vendor`] = {
      message: ERROR_MESSAGES["missingVendorName"],
    };
  }

  return {
    values: !isEmpty(errors) ? {} : values,
    errors,
  };
};

type CashToSchoolProps = {
  purchaseCurrentStoreData: PurchaseEvent;
  purchased_at: boolean;
};
const CashToSchool: React.ElementType<CashToSchoolProps> = ({
  purchaseCurrentStoreData,
  purchased_at,
}) => {
  const {
    updateStore,
    closeModal,
    submitBtnRef,
    submitViaBtnRefClick,
  } = useStore();
  const intl = useIntl();
  const state = useSelector((state) => state) as RootState;
  const vendorCategories = useSelector(getVendorCategories);
  const vendorList = useSelector(getAllApprovedVendors);
  const otherCost = useSelector(getPurchaseOtherCostsList);
  const currentYearStore = useSelector((state: RootState) =>
    getCurrentSchoolYear(state)
  );
  const previousSchoolYears = useSelector(getPreviousSchoolYears);
  const defaultValues = purchaseCurrentStoreData
    ? storeToFormValues(
        purchaseCurrentStoreData,
        vendorCategories.results,
        vendorList.results,
        otherCost.results,
        purchased_at
      )
    : { stock_events: [{}], other_costs: [{}] };

  const stockStatus = useSelector(
    getDetailedStockStatus(moment().format("YYYY-MM-DD"))
  );

  const methods = useForm({
    defaultValues,
    resolver: validationResolver,
    context: {
      state,
      purchaseCurrentStoreData,
      stockStatus,
      defaultValues,
      currentYearStore,
      previousSchoolYears,
    },
  });
  const {
    control,
    handleSubmit,
    errors,
    watch,
    setValue,
    getValues,
    register,
  } = methods;
  const selectedDate = watch("date");
  const isSelectedDateInSchoolYearValid = schoolYearDateValidation(
    selectedDate,
    currentYearStore!,
    previousSchoolYears
  );
  const isErrorListEmpty = isEmpty(errors);

  const onSubmit = (values: any) => {
    updateStore({
      id: !purchased_at
        ? (purchaseCurrentStoreData && purchaseCurrentStoreData.client_id) ||
          undefined
        : undefined,
      values: {
        // A repeat purchase creates a new purchase and must not inherit the
        // initial stock flags of the purchase it was repeated from
        ...formValuesToStore(
          values,
          purchased_at ? undefined : purchaseCurrentStoreData
        ),
        event_type: "purchase_detail",
      },
      type:
        purchaseCurrentStoreData && purchaseCurrentStoreData.client_id
          ? "update"
          : "create",
      category: purchaseDetailCategory,
    });
  };
  return (
    <ModalExtended
      open
      hideClose
      onRequestSubmit={submitViaBtnRefClick}
      onRequestClose={() => {}}
      onSecondarySubmit={() => closeModal()}
      modalLabel={intl.formatMessage({
        id: "PurchaseEdit.purchase",
        defaultMessage: "School purchase",
      })}
      modalHeading={intl.formatMessage({
        id: "PurchaseEdit.purchaseDetails",
        defaultMessage: "Purchase details",
      })}
      wide
      primaryButtonText={intl.formatMessage({
        id: "PurchaseEdit.savePurchase",
        defaultMessage: "Save purchase",
      })}
      secondaryButtonText={intl.formatMessage({
        id: "Form.cancel",
        defaultMessage: "Cancel",
      })}
      selectorPrimaryFocus={false}
      className="cash-to-school"
    >
      <FormProvider {...methods}>
        {!isSelectedDateInSchoolYearValid && isErrorListEmpty && (
          <Blockquote kind="warning" className="date-not-valid">
            {ERROR_MESSAGES_INCIDENTS.invalidDate}
          </Blockquote>
        )}
        {isReportClosedOrRejected(state, selectedDate) && isErrorListEmpty && (
          <Blockquote kind="warning" className="date-not-valid">
            {ERROR_MESSAGES_INCIDENTS.reportClosedOrRejected}
          </Blockquote>
        )}
        {isReportApprovedOrSubmitted(state, selectedDate) && isErrorListEmpty && (
          <Blockquote kind="warning" className="date-not-valid">
            {ERROR_MESSAGES_INCIDENTS.reportApprovedOrSubmitted}
          </Blockquote>
        )}
        <ErrorList errors={errors} />
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="wfp--form wfp--form-long"
        >
          <VendorEdit
            control={control}
            watch={watch}
            errors={errors}
            setValue={setValue}
            getValues={getValues}
            register={register}
            purchaseCurrentStoreData={purchaseCurrentStoreData}
          />

          <PurchaseInformation
            errors={errors}
            currentYearStore={currentYearStore}
            control={control}
            register={register}
          />

          <CommoditiesEdit
            watch={watch}
            errors={errors}
            control={control}
            setValue={setValue}
            getValues={getValues}
            register={register}
            purchaseCurrentStoreData={purchaseCurrentStoreData}
            stockStatus={stockStatus}
          />

          <button className="hidden-btn" ref={submitBtnRef as any} />
        </form>
      </FormProvider>
    </ModalExtended>
  );
};

export default CashToSchool;
